// UnmappedAlert.js
import React, { useEffect, useState, useContext } from 'react';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext';
import { ArrowRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export default function UnmappedAlert({ variant = 'banner' }) {
  const { user } = useContext(AuthContext);
  const [unmappedCount, setUnmappedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchUnmappedCount = async () => {
      try {
        // Fetch all measurement IDs
        const { data: allMeasurements, error: measurementsError } = await supabase
          .from('measurements')
          .select('id');
        if (measurementsError) {
          console.error('Error fetching measurements:', measurementsError);
          return;
        }

        // Fetch mapped measurement IDs from project_mapping table
        const { data: mappings, error: mappingError } = await supabase
          .from('project_mapping')
          .select('measurement_id');
        if (mappingError) {
          console.error('Error fetching mappings:', mappingError);
          return;
        }

        // Filter out the mapped measurements
        const mappedIds = new Set(mappings.map((m) => m.measurement_id));
        const unmappedMeasurements = allMeasurements.filter((m) => !mappedIds.has(m.id));

        setUnmappedCount(unmappedMeasurements.length);
      } catch (error) {
        console.error('Unexpected error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUnmappedCount();
  }, [user]);

  // While counting, show nothing or a spinner
  if (loading) return null;

  // If there are no unmapped measurements, don't render anything
  if (unmappedCount === 0) return null;

  // ========== RENDER AS BANNER ==========
  if (variant === 'banner') {
    return (
      <div
        className="border-l-4 border-yellow-400 bg-yellow-50 p-4 mt-2 max-w-7xl mx-auto"
        role="alert"
      >
        <div className="flex">
          <div className="shrink-0">
            <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              You have {unmappedCount} unmapped measurement
              {unmappedCount > 1 ? 's' : ''}.{' '}
              <button
                onClick={() => (window.location.href = '/unmappedproject')}
                className="font-bold underline"
              >
                Click here to map them
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ========== RENDER AS BUTTON ==========
  if (variant === 'button') {
    return (
      <a
        href="/unmappedproject"
        className="flex items-center justify-center w-full px-4 py-2 bg-yellow-600 text-white font-medium text-xs rounded-md shadow-sm hover:bg-yellow-500 transition-colors"
      >
        <ExclamationTriangleIcon aria-hidden="true" className="h-5 w-5 mr-2 text-yellow-400" />
        Unmapped ({unmappedCount})
      </a>
    );
  }  

  // Default to no render if variant is unrecognized
  return null;
}
